import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { hasAny } from '@/utils/permissions';
import type { RoleCode } from '@/types';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    layout?: 'default' | 'blank';
    title?: string;
    roles?: RoleCode[];
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { layout: 'blank', title: '登录' },
  },
  {
    path: '/apply',
    name: 'public-apply',
    component: () => import('@/views/public/ApplyView.vue'),
    meta: { layout: 'blank', title: '门店入驻申请' },
  },
  {
    path: '/',
    redirect: '/cashier',
  },
  {
    path: '/cashier',
    name: 'cashier',
    component: () => import('@/views/cashier/CashierView.vue'),
    meta: { requiresAuth: true, title: '收银', roles: ['store_admin', 'store_cashier'] },
  },
  {
    path: '/cashier/receipts',
    name: 'cashier-receipts',
    component: () => import('@/views/cashier/ReceiptListView.vue'),
    meta: { requiresAuth: true, title: '收银流水', roles: ['store_admin', 'store_cashier'] },
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/views/customers/CustomerListView.vue'),
    meta: { requiresAuth: true, title: '客户档案' },
  },
  {
    path: '/customers/:id',
    name: 'customer-detail',
    component: () => import('@/views/customers/CustomerDetailView.vue'),
    meta: { requiresAuth: true, title: '客户详情' },
  },
  {
    path: '/consume',
    name: 'consume-register',
    component: () => import('@/views/consume/ConsumeRegisterView.vue'),
    meta: { requiresAuth: true, title: '消费登记', roles: ['store_admin', 'store_cashier'] },
  },
  {
    path: '/inventory',
    name: 'inventory',
    component: () => import('@/views/inventory/StockLedgerView.vue'),
    meta: { requiresAuth: true, title: '库存台账' },
  },
  {
    path: '/inventory/movements',
    name: 'inventory-movements',
    component: () => import('@/views/inventory/StockMovementView.vue'),
    meta: { requiresAuth: true, title: '库存流水' },
  },
  {
    path: '/inventory/stocktake',
    name: 'inventory-stocktake',
    component: () => import('@/views/inventory/StocktakeView.vue'),
    meta: { requiresAuth: true, title: '盘点', roles: ['store_admin'] },
  },
  {
    path: '/inventory/low-stock',
    name: 'inventory-low-stock',
    component: () => import('@/views/inventory/LowStockAlertView.vue'),
    meta: { requiresAuth: true, title: '低库存预警' },
  },
  {
    path: '/replenish',
    name: 'replenish',
    component: () => import('@/views/replenish/ReplenishView.vue'),
    meta: { requiresAuth: true, title: '补货下单', roles: ['store_admin'] },
  },
  {
    path: '/replenish/orders',
    name: 'replenish-orders',
    component: () => import('@/views/replenish/ReplenishOrdersView.vue'),
    meta: { requiresAuth: true, title: '补货订单', roles: ['store_admin'] },
  },
  {
    path: '/b-account',
    name: 'b-account',
    component: () => import('@/views/replenish/BAccountView.vue'),
    meta: { requiresAuth: true, title: 'B 端账户', roles: ['store_admin'] },
  },
  {
    path: '/refund',
    name: 'refund',
    component: () => import('@/views/refund/PackageRefundView.vue'),
    meta: { requiresAuth: true, title: '套餐退款', roles: ['store_admin'] },
  },
  {
    path: '/swap',
    name: 'swap',
    component: () => import('@/views/refund/PackageSwapView.vue'),
    meta: { requiresAuth: true, title: '套餐置换', roles: ['store_admin'] },
  },
  {
    path: '/detection',
    name: 'detection',
    component: () => import('@/views/detection/DetectionListView.vue'),
    meta: { requiresAuth: true, title: '检测中心' },
  },
  {
    path: '/detection/:id',
    name: 'detection-detail',
    component: () => import('@/views/detection/DetectionReportView.vue'),
    meta: { requiresAuth: true, title: '检测报告' },
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('@/views/devices/DeviceListView.vue'),
    meta: { requiresAuth: true, title: '设备状态' },
  },
  {
    path: '/devices/faults',
    name: 'device-faults',
    component: () => import('@/views/devices/DeviceFaultView.vue'),
    meta: { requiresAuth: true, title: '故障工单' },
  },
  {
    path: '/technicians',
    name: 'technicians',
    component: () => import('@/views/technicians/TechnicianListView.vue'),
    meta: { requiresAuth: true, title: '技师管理', roles: ['store_admin'] },
  },
  {
    path: '/operators',
    name: 'operators',
    component: () => import('@/views/operators/OperatorListView.vue'),
    meta: { requiresAuth: true, title: '员工账号', roles: ['store_admin'] },
  },
  {
    path: '/promotions',
    name: 'promotions',
    component: () => import('@/views/promotions/PromotionApplyView.vue'),
    meta: { requiresAuth: true, title: '促销申请', roles: ['store_admin'] },
  },
  {
    path: '/settlement',
    name: 'settlement',
    component: () => import('@/views/settlement/SettlementView.vue'),
    meta: { requiresAuth: true, title: '跨店结算', roles: ['store_admin'] },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/dashboard/DashboardView.vue'),
    meta: { requiresAuth: true, title: '数据看板', roles: ['store_admin'] },
  },
  {
    path: '/technician-stats',
    name: 'technician-stats',
    component: () => import('@/views/technician-stats/TechnicianStatsView.vue'),
    meta: { requiresAuth: true, title: '技师KPI', roles: ['store_admin'] },
  },
  {
    path: '/schedule',
    name: 'schedule',
    component: () => import('@/views/schedule/ScheduleView.vue'),
    meta: { requiresAuth: true, title: '技师排班', roles: ['store_admin'] },
  },
  {
    path: '/appointments',
    name: 'appointments',
    component: () => import('@/views/appointments/AppointmentListView.vue'),
    meta: { requiresAuth: true, title: '预约管理', roles: ['store_admin'] },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const auth = useAuthStore();
    await auth.bootstrap();

    if (to.meta.requiresAuth && !auth.isAuthenticated) {
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }

    if (to.name === 'login' && auth.isAuthenticated) {
      return next('/');
    }

    if (to.meta.roles && to.meta.roles.length > 0 && !hasAny(auth.roles, to.meta.roles)) {
      return next('/');
    }

    if (typeof to.meta.title === 'string') {
      document.title = `${to.meta.title} · 丝馥生 门店`;
    }

    return next();
  },
);
